import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { db } from "../../firebase";
import PageHeader from "../../components/PageHeader";

type UserRow = {
  uid: string;
  displayName: string;
  email?: string;
  wins: number;
  totalGames: number;
  banned: boolean;
  reportCount: number;
};

function toRow(d: { id: string; data: () => Record<string, unknown> }): UserRow {
  const x = d.data();
  return {
    uid: d.id,
    displayName: (x.displayName as string) ?? "—",
    email: x.email as string | undefined,
    wins: Number(x.wins ?? 0),
    totalGames: Number(x.totalGames ?? 0),
    banned: Boolean(x.banned),
    reportCount: Number(x.reportCount ?? 0),
  };
}

export default function Users() {
  const [input, setInput] = useState("");
  const [rows, setRows] = useState<UserRow[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  /**
   * Arama yapılmadan da son kaydolanları göster. Önceden sayfa boş açılıyordu
   * ve kimin olduğunu bilmeden arama yapmak gerekiyordu.
   */
  const loadRecent = async () => {
    setBusy(true);
    setErr(null);
    try {
      const snap = await getDocs(
        query(collection(db, "users"), orderBy("createdAt", "desc"), limit(50))
      );
      setRows(snap.docs.map(toRow));
      setSearching(false);
    } catch (e) {
      setErr("Liste alınamadı: " + (e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    void loadRecent();
  }, []);

  const runSearch = async () => {
    const q = input.trim().toLowerCase();
    if (q.length < 2) {
      setErr("En az 2 karakter");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const snap = await getDocs(
        query(
          collection(db, "users"),
          where("displayNameLower", ">=", q),
          where("displayNameLower", "<", q + "\uf8ff"),
          orderBy("displayNameLower"),
          limit(50)
        )
      );
      setRows(snap.docs.map(toRow));
      setSearching(true);
    } catch (e) {
      setErr("Arama başarısız: " + (e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Kullanıcılar"
        subtitle="Son kaydolanlar listeleniyor. Oyuncu adı ile arayabilir veya UID'yi doğrudan girebilirsin."
      />

      <div className="card p-4 mb-4">
        <div className="flex gap-2">
          <input
            className="input flex-1"
            placeholder="Oyuncu adı (en az 2 harf)"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") runSearch();
            }}
          />
          <button
            disabled={busy}
            onClick={runSearch}
            className="btn-primary disabled:opacity-60"
          >
            {busy ? "…" : "Ara"}
          </button>
          {searching && (
            <button
              className="btn-outline"
              disabled={busy}
              onClick={() => {
                setInput("");
                void loadRecent();
              }}
            >
              Tümü
            </button>
          )}
        </div>
        {input.trim().length >= 20 && (
          <Link
            to={`/admin/users/${input.trim()}`}
            className="text-xs font-bold text-primarySoft hover:underline mt-3 inline-block"
          >
            Bunu UID olarak aç →
          </Link>
        )}
        {err && (
          <div className="text-sm text-danger font-semibold mt-3">{err}</div>
        )}
      </div>

      {rows && (
        <>
          <div className="flex items-baseline justify-between mb-2 px-1">
            <span className="section-label">
              {searching ? "Arama sonucu" : "Son kaydolanlar"}
            </span>
            <span className="text-[11px] text-textMuted tabular-nums">
              {rows.length} kayıt
            </span>
          </div>
          <div className="card divide-y divide-stroke/70">
            {rows.length === 0 ? (
              <div className="p-8 text-center text-textMuted">
                {searching
                  ? "Eşleşen kullanıcı yok."
                  : "Henüz kayıtlı oyuncu yok."}
              </div>
            ) : (
            rows.map((u) => (
              <Link
                key={u.uid}
                to={`/admin/users/${u.uid}`}
                className="flex items-center gap-4 p-4 hover:bg-surfaceHigh/50 transition"
              >
                <div
                  className={[
                    "h-10 w-10 rounded-full grid place-items-center font-extrabold text-white",
                    u.banned
                      ? "bg-gradient-to-br from-danger to-rose-700"
                      : "bg-gradient-to-br from-primary to-primaryDeep",
                  ].join(" ")}
                >
                  {u.displayName.charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-extrabold truncate">
                      {u.displayName}
                    </span>
                    {u.banned && (
                      <span className="pill bg-danger/20 text-danger border border-danger/40">
                        BANLI
                      </span>
                    )}
                    {u.reportCount > 0 && (
                      <span className="pill bg-warning/15 text-warning border border-warning/40">
                        {u.reportCount} rapor
                      </span>
                    )}
                  </div>
                  {u.email && (
                    <div className="text-xs text-textMuted truncate">
                      {u.email}
                    </div>
                  )}
                  <div className="text-xs text-textMuted font-mono truncate">
                    {u.uid}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-extrabold text-lg leading-none text-gold">
                    {u.wins}
                  </div>
                  <div className="text-[10px] tracking-widest text-textMuted uppercase mt-0.5">
                    galibiyet · {u.totalGames} maç
                  </div>
                </div>
                </Link>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}
